import { convert } from "@/libs/units";
import {
  Severity,
  valuesExist,
  addStackPrefixToMessage,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import {
  ABRASIVE_MAX_FV,
  MAX_FLOW_VELOCITY,
  MAX_MOTOR_SKIN_TEMP,
  MIN_CLEARANCE,
  MIN_FLOW_VELOCITY,
  MIN_STARTUP_VOLTAGE_RATIO
} from "../validatorConstants";
import { PercentageUnits } from "@/libs/units/percentage";
import { MIN_MOTOR_LOAD_RATIO } from "@/libs/calculations/constants";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";

export const motorChecksNames = [
  "motor_power",
  "startup_voltage",
  "flow_velocity_around_motor_low",
  "flow_velocity_around_motor_high",
  "flow_velocity_around_motor_high_abrasive",
  "motor_load_ratio",
  "motor_under_load_ratio",
  "motor_skin_temp",
  "motor_size_top_casing",
  "motor_size_bypass_tubing_top_casing",
  "motor_size_bottom_casing",
  "motor_size_bypass_tubing_bottom_casing",
  "motor_stage_temp",
  "motor_lock_plate",
  "motor_rerate_range",
  "gas_specific_gravity_zero",
  "hps_motor_with_espshore_selected",
  "espshore_motor_with_hps_selected"
] as const;

export const motorChecks: ValidationRuleGenerator = ({
  calcs,
  equipment,
  wellProperties,
  parameters,
  stack
}) => [
  // Motor HP check
  {
    name: "motor_power",
    message: addStackPrefixToMessage(
      `Motor must be able to generate at least ${calcs.motorCalculations.totalRequired60HzBrakeHP.value.toFixed(
        0
      )}hp`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      equipment.value?.motor_id &&
      valuesExist([
        calcs.motorCalculations.reratedNameplateHP.value,
        calcs.motorCalculations.totalRequired60HzBrakeHP.value
      ]),
    isInvalid:
      calcs.motorCalculations.reratedNameplateHP.value <
      calcs.motorCalculations.totalRequired60HzBrakeHP.value,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  // Startup voltage check
  // This will get reimplemented with updated formulas
  {
    name: "startup_voltage",
    message: addStackPrefixToMessage(
      `Startup Voltage must be at least ${convert(
        MIN_STARTUP_VOLTAGE_RATIO,
        PercentageUnits.Ratio,
        PercentageUnits.Percentage
      )}% of operating volts`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      equipment.value?.motor_id &&
      equipment.value?.cables_order?.length &&
      valuesExist([calcs.surfaceDriveCalculations.startUpVoltageNamplateRatio]),
    isInvalid:
      calcs.surfaceDriveCalculations.startUpVoltageNamplateRatio.value <
      MIN_STARTUP_VOLTAGE_RATIO,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },

  // Flow velocity check
  {
    name: "flow_velocity_around_motor_low",
    message: addStackPrefixToMessage(
      `Flow velocity around the motor is less than ${MIN_FLOW_VELOCITY} ft/sec (could overheat motor)`,
      stack,
      equipment
    ),
    severity: Severity.warn,
    isActive:
      equipment.value?.motor_id &&
      valuesExist([calcs.motorCalculations.flowVelocityAroundMotorFTSEC]) &&
      calcs.isScenarioEspShore.value,
    isInvalid:
      calcs.motorCalculations.flowVelocityAroundMotorFTSEC.value <
      MIN_FLOW_VELOCITY,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "flow_velocity_around_motor_high",
    message: addStackPrefixToMessage(
      `Flow velocity around the motor is greater than ${MAX_FLOW_VELOCITY} ft/sec (can erode motor)`,
      stack,
      equipment
    ),
    severity: Severity.warn,
    isActive:
      valuesExist([calcs.motorCalculations.flowVelocityAroundMotorFTSEC]) &&
      calcs.isScenarioEspShore.value,
    isInvalid:
      calcs.motorCalculations.flowVelocityAroundMotorFTSEC.value >
      MAX_FLOW_VELOCITY,
    disablesRules: ["flow_velocity_around_motor_high_abrasive"],
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "flow_velocity_around_motor_high_abrasive",
    message: addStackPrefixToMessage(
      `Flow velocity around the motor is greater than ${ABRASIVE_MAX_FV} ft/sec (only problematic with abrasive fluids)`,
      stack,
      equipment
    ),
    severity: Severity.warn,
    isActive:
      valuesExist([calcs.motorCalculations.flowVelocityAroundMotorFTSEC]) &&
      calcs.isScenarioEspShore.value,
    isInvalid:
      calcs.motorCalculations.flowVelocityAroundMotorFTSEC.value >
      ABRASIVE_MAX_FV,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },

  // Motor Load check
  {
    name: "motor_load_ratio",
    message: addStackPrefixToMessage(
      `HP Required Exceeds Motor Rating`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      equipment.value?.motor_id,
      calcs.motorCalculations.motorLoadRatio
    ]),
    isInvalid: calcs.motorCalculations.motorLoadRatio.value > 1,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },

  // Motor Under Load Ratio
  {
    name: "motor_under_load_ratio",
    message: addStackPrefixToMessage(
      `Motor Underload - Motor Load is below ${PercentageUnits.Ratio.toStandardUnit(
        MIN_MOTOR_LOAD_RATIO
      )}% `,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      equipment.value?.motor_id,
      calcs.motorCalculations.motorLoadRatio
    ]),
    isInvalid:
      calcs.motorCalculations.motorLoadRatio.value < MIN_MOTOR_LOAD_RATIO,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },

  // Motor Skin Temp Check
  {
    name: "motor_skin_temp",
    message: addStackPrefixToMessage(
      `Motor skin temperature is greater than ${MAX_MOTOR_SKIN_TEMP}F"`,
      stack,
      equipment
    ),
    severity: Severity.warn,
    isActive: valuesExist([
      equipment.value?.motor_id,
      calcs.motorCalculations.motorInternalTemperatureF,
      equipment.value?.r_motor?.temperature_rating_fahrenheit
    ]),
    isInvalid:
      calcs.motorCalculations.motorInternalTemperatureF.value >
      (equipment.value?.r_motor?.temperature_rating_fahrenheit || 0),
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  // Size check
  {
    name: "motor_size_top_casing",
    message: addStackPrefixToMessage(
      `Clearance Error: Motor too large for top casing`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      valuesExist([
        wellProperties.value?.r_top_casing?.drift_inches,
        equipment.value?.r_motor?.id,
        wellProperties.value?.top_casing_length_feet
      ]) && !equipment.value?.r_shroud?.interior_diameter_inches,
    isInvalid:
      wellProperties.value?.r_top_casing?.drift_inches -
        equipment.value?.r_motor?.outer_diameter_rub_inches <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "motor_size_bypass_tubing_top_casing",
    message: addStackPrefixToMessage(
      `Clearance Error: Motor too large with bypass tubing for top casing`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      valuesExist([
        wellProperties.value?.r_top_casing?.drift_inches,
        equipment.value?.r_motor?.id,
        wellProperties.value?.top_casing_length_feet
      ]) &&
      !equipment.value?.r_shroud?.interior_diameter_inches &&
      !!equipment.value?.use_uphole_y_tool &&
      calcs.orderedYToolBypassTubings.value.length > 0,
    isInvalid:
      wellProperties.value?.r_top_casing?.drift_inches -
        (equipment.value?.r_motor?.outer_diameter_rub_inches +
          calcs.yToolBypassTubingMaxODIn.value) <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "motor_size_bottom_casing",
    message: addStackPrefixToMessage(
      `Clearance Error: Motor too large for bottom casing`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      valuesExist([
        wellProperties.value?.r_bottom_casing?.drift_inches,
        equipment.value?.r_motor?.id,
        wellProperties.value?.bottom_casing_length_feet
      ]) &&
      calcs.equipmentLengths.bottomOfMotorDepthFt.value >
        wellProperties.value?.top_casing_length_feet &&
      !equipment.value?.r_shroud?.interior_diameter_inches,
    isInvalid:
      wellProperties.value?.r_bottom_casing?.drift_inches -
        equipment.value?.r_motor?.outer_diameter_rub_inches <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "motor_size_bypass_tubing_bottom_casing",
    message: addStackPrefixToMessage(
      `Clearance Error: Motor too large with bypass tubing for bottom casing`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      valuesExist([
        wellProperties.value?.r_bottom_casing?.drift_inches,
        equipment.value?.r_motor?.id,
        wellProperties.value?.bottom_casing_length_feet
      ]) &&
      calcs.equipmentLengths.bottomOfMotorDepthFt.value >
        wellProperties.value?.top_casing_length_feet &&
      !equipment.value?.r_shroud?.interior_diameter_inches &&
      !!equipment.value?.use_uphole_y_tool &&
      calcs.orderedYToolBypassTubings.value.length > 0,
    isInvalid:
      wellProperties.value?.r_bottom_casing?.drift_inches -
        (equipment.value?.r_motor?.outer_diameter_rub_inches +
          calcs.yToolBypassTubingMaxODIn.value) <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  // Windings Temp check
  {
    name: "motor_stage_temp",
    message: addStackPrefixToMessage(
      `Motor internal temperature is greater than the motor temp rating`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      calcs.motorCalculations.motorInternalTemperatureF,
      equipment.value?.motor_id
    ]),
    isInvalid:
      calcs.motorCalculations.motorInternalTemperatureF.value >
      (equipment.value?.r_motor?.temperature_rating_fahrenheit || 0),
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },

  // Motor HP check
  {
    name: "motor_lock_plate",
    message: addStackPrefixToMessage(
      `+300 HP Requires Lock Plates (Increasing OD) or Seam Welded`,
      stack,
      equipment
    ),
    severity: Severity.warn,
    isActive:
      valuesExist([
        calcs.motorCalculations.reratedNameplateHP,
        equipment.value?.motor_id
      ]) && calcs.isScenarioEspShore.value,
    isInvalid: calcs.motorCalculations.reratedNameplateHP.value >= 300,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  // Motor rerate warning
  {
    name: "motor_rerate_range",
    message: addStackPrefixToMessage(
      `Motor rerate is over 100%, calcs may be unreliable`,
      stack,
      equipment
    ),
    severity: Severity.warn,
    isActive: valuesExist([parameters.value?.motor_rerate_ratio]),
    isInvalid: parameters.value?.motor_rerate_ratio > 1,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "gas_specific_gravity_zero",
    message: addStackPrefixToMessage(
      "Gas Specific Gravity cannot be 0",
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([calcs.basicData.value.gasSpecificGravity]),
    isInvalid:
      calcs.basicData.value.gasSpecificGravityOverride === 0 &&
      calcs.basicData.value.gasSpecificGravity === 0,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "hps_motor_with_espshore_selected",
    message: addStackPrefixToMessage(
      "Not Recommended: HPS Motor with ESP Shore",
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.motor_id]),
    isInvalid:
      calcs.isScenarioEspShore.value && equipment.value?.r_motor?.is_hps,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "espshore_motor_with_hps_selected",
    message: addStackPrefixToMessage(
      "Not Recommended: HPS Pump type with ESP Shore Motor",
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.motor_id]),
    isInvalid:
      !equipment.value?.r_motor?.is_hps && !calcs.isScenarioEspShore.value,
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  }
];
